import { createApiInstance } from './axios-config';
import API_CONFIG from '../../config/apiConfig';

const api = createApiInstance(API_CONFIG.INTEGRATION_SERVICE_URL);

export interface Integration {
  id: string;
  userId: string;
  provider: string;
  status: 'connected' | 'disconnected' | 'error';
  email?: string;
  connectedAt?: Date;
  lastSync?: Date;
  scopes?: string[];
  metadata?: Record<string, any>;
}

export interface AvailableIntegration {
  type: string;
  name: string;
  integration_type: 'email_only' | 'full';
  min_subscription_tier: string;
  access_status: 'available' | 'upgrade_required';
  required_tier_name?: string;
  required_tier_price?: number;
  scopes?: string[];
}

export interface OAuthResponse {
  authUrl: string;
  state: string;
}

export interface SyncResult {
  success: boolean;
  itemsSynced: {
    emails?: number;
    events?: number;
    documents?: number;
    contacts?: number;
  };
  errors?: string[];
}

export class IntegrationService {
  private baseUrl: string = '/api/v1/integrations';

  async getIntegrations(): Promise<Integration[]> {
    const response = await api.get(`${this.baseUrl}/`);
    return response.data.integrations || [];
  }

  async getIntegration(provider: string): Promise<Integration | null> {
    try {
      const response = await api.get(`${this.baseUrl}/${provider}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getOAuthUrl(provider: string): Promise<OAuthResponse> {
    const response = await api.post(`${this.baseUrl}/${provider}/oauth/init`);
    return response.data;
  }

  async handleOAuthCallback(provider: string, code: string, state: string): Promise<Integration> {
    const response = await api.post(`${this.baseUrl}/${provider}/oauth/callback`, {
      code,
      state,
    });
    return response.data;
  }

  async disconnectIntegration(provider: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${provider}`);
  }

  async syncIntegration(provider: string, options?: {
    resources?: string[];
    fromDate?: Date;
    toDate?: Date;
  }): Promise<SyncResult> {
    const response = await api.post(`${this.baseUrl}/${provider}/sync`, options);
    return response.data;
  }

  async testIntegration(provider: string): Promise<{ success: boolean; message: string }> {
    const response = await api.get(`${this.baseUrl}/${provider}/test`);
    return response.data;
  }

  // Microsoft-specific methods
  async getMicrosoftEmails(options?: {
    folder?: string;
    limit?: number;
    offset?: number;
  }): Promise<any[]> {
    const response = await api.get(`${this.baseUrl}/microsoft/emails`, { params: options });
    return response.data.emails || [];
  }

  async getMicrosoftCalendarEvents(options?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
  }): Promise<any[]> {
    const response = await api.get(`${this.baseUrl}/microsoft/events`, { params: options });
    return response.data.events || [];
  }

  async getMicrosoftFiles(options?: {
    folderId?: string;
    limit?: number;
  }): Promise<any[]> {
    const response = await api.get(`${this.baseUrl}/microsoft/files`, { params: options });
    return response.data.files || [];
  }

  // Google-specific methods
  async getGoogleEmails(options?: {
    label?: string;
    limit?: number;
    pageToken?: string;
  }): Promise<any> {
    const response = await api.get(`${this.baseUrl}/google/emails`, { params: options });
    return response.data;
  }

  async getGoogleCalendarEvents(options?: {
    calendarId?: string;
    timeMin?: string;
    timeMax?: string;
    limit?: number;
  }): Promise<any[]> {
    const response = await api.get(`${this.baseUrl}/google/events`, { params: options });
    return response.data.events || [];
  }

  async getGoogleDriveFiles(options?: {
    folderId?: string;
    mimeType?: string;
    limit?: number;
  }): Promise<any[]> {
    const response = await api.get(`${this.baseUrl}/google/files`, { params: options });
    return response.data.files || [];
  }

  // Get available integrations with subscription check
  async getAvailableIntegrations(): Promise<{
    subscriptionTier: string;
    subscriptionStatus: string;
    integrations: AvailableIntegration[];
  }> {
    const response = await api.get('/api/v1/email-sync/available');
    return response.data;
  }

  // Sync emails to knowledge graph
  async syncEmailsToKnowledgeGraph(integrationId: string): Promise<void> {
    await api.post(`/api/v1/email-sync/${integrationId}/sync`);
  }

  // Get email sync status
  async getEmailSyncStatus(integrationId: string): Promise<any> {
    const response = await api.get(`/api/v1/email-sync/${integrationId}/sync/status`);
    return response.data;
  }

  // IMAP Connection Methods
  async getIMAPPresets(): Promise<{ presets: any[] }> {
    const response = await api.get('/api/v1/imap/presets');
    return response.data;
  }

  async getIMAPConnections(): Promise<{ connections: any[] }> {
    const response = await api.get('/api/v1/imap/connections');
    return response.data;
  }

  async createIMAPConnection(connection: any): Promise<any> {
    const response = await api.post('/api/v1/imap/connections', connection);
    return response.data;
  }

  async updateIMAPConnection(connectionId: string, updates: any): Promise<any> {
    const response = await api.put(`/api/v1/imap/connections/${connectionId}`, updates);
    return response.data;
  }

  async deleteIMAPConnection(connectionId: string): Promise<void> {
    await api.delete(`/api/v1/imap/connections/${connectionId}`);
  }

  async testIMAPConnection(config: any): Promise<any> {
    const response = await api.post('/api/v1/imap/test', config);
    return response.data;
  }

  async syncIMAPEmails(connectionId: string): Promise<any> {
    const response = await api.post(`/api/v1/imap/connections/${connectionId}/sync`);
    return response.data;
  }

  async getIMAPSyncStatus(connectionId: string, jobId?: string): Promise<any> {
    const params = jobId ? { jobId } : {};
    const response = await api.get(`/api/v1/imap/connections/${connectionId}/sync-status`, { params });
    return response.data;
  }

  async syncIMAPToKnowledgeGraph(connectionId: string): Promise<any> {
    const response = await api.post(`/api/v1/imap/connections/${connectionId}/sync-to-kg`);
    return response.data;
  }

  // OAuth Configuration Methods (for admins/owners)
  async getOAuthConfigs(): Promise<any[]> {
    const response = await api.get('/api/v1/oauth/configs');
    return response.data.configs || [];
  }

  async createOAuthConfig(config: {
    provider: string;
    clientId: string;
    clientSecret: string;
    redirectUri: string;
    scopes: string[];
    isActive: boolean;
  }): Promise<any> {
    const response = await api.post('/api/v1/oauth/configs', config);
    return response.data;
  }

  async updateOAuthConfig(id: string, config: {
    clientId?: string;
    clientSecret?: string;
    redirectUri?: string;
    scopes?: string[];
    isActive?: boolean;
  }): Promise<any> {
    const response = await api.put(`/api/v1/oauth/configs/${id}`, config);
    return response.data;
  }

  async deleteOAuthConfig(id: string): Promise<void> {
    await api.delete(`/api/v1/oauth/configs/${id}`);
  }
}